import {
  CANADA_COUNTRY_CODE,
  FRENCH_LOCALE,
  GERMAN_LOCALE,
  GERMANY_COUNTRY_CODE,
  QUEBEC_REGION_CODE,
} from "@/core/constants/language-and-currency";
import { useContentOverlayLocale } from "./useContentOverlayLocale";
import { useGeolocation } from "./useGeolocation";
import type { IContentOverlayLocale } from "./useContentOverlayLocale";

/**
 * Registry of every content-overlay locale (German, French, …).
 *
 * An overlay locale is content served via `cultureName` and carried in a `/{locale}/` URL prefix
 * WITHOUT switching the app-wide culture. Each entry differs only in its locale code, its 30-day
 * suppression-timestamp key, and its geolocation auto-select trigger; all behavior is shared by
 * {@link useContentOverlayLocale}.
 *
 * This is the single source of truth: the language selector, footer, and matcher all iterate this
 * list, so adding a future overlay locale is config-only — append one entry here (and its
 * constants, including the static `OVERLAY_LOCALES` codes used by pure path logic in `useLanguages`)
 * and no call site needs per-locale code.
 */
export function useOverlayLocales(): IContentOverlayLocale[] {
  const { countryCodeISO2: country, regionCode } = useGeolocation();

  return [
    useContentOverlayLocale({
      locale: GERMAN_LOCALE,
      timestampStorageKey: "germanTimestamp",
      autoSelectWhen: () => country.value === GERMANY_COUNTRY_CODE,
    }),
    useContentOverlayLocale({
      locale: FRENCH_LOCALE,
      timestampStorageKey: "frenchTimestamp",
      // French auto-selects only for visitors geolocated to the province of Quebec, Canada.
      autoSelectWhen: () => country.value === CANADA_COUNTRY_CODE && regionCode.value === QUEBEC_REGION_CODE,
    }),
  ];
}
